import { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';

// Flatten the (transparent) signature canvas onto an opaque white background. A
// bare canvas exports a transparent PNG whose dark strokes can vanish — reading as
// a blank/"white" image — once shown on a non-white surface or after a downstream
// step drops its alpha. White-backing makes it render the same everywhere.
function exportWhitePng(src) {
    const flat = document.createElement('canvas');
    flat.width = src.width;
    flat.height = src.height;
    const ctx = flat.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, flat.width, flat.height);
    ctx.drawImage(src, 0, 0);
    return flat.toDataURL('image/png');
}

/**
 * Lightweight canvas signature pad (mouse, pen & touch via pointer events), no external deps.
 * Calls onChange with a PNG data-URL after each stroke, or null when cleared.
 */
export default function SignaturePad({ onChange, height = 150, label = 'Sign here' }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const hasInk = useRef(false);
    const [isEmpty, setIsEmpty] = useState(true);

    // Match the canvas bitmap to its rendered size and (re)apply stroke styles.
    // Resizing a canvas clears it, so this is only ever called while the pad is
    // empty (initial mount, on resize, and just before the first stroke).
    const prepareCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        if (!w || !h) return; // not laid out yet (e.g. dialog still animating in)
        if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
        }
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }, []);

    useEffect(() => {
        prepareCanvas();
        // Re-measure when the pad's box changes size — it commonly mounts inside a
        // dialog that is still animating (zero/scaled width) at first paint, which
        // otherwise sizes the bitmap wrong and yields a blank/"white" signature.
        // Guarded by hasInk so a resize never wipes a signature in progress.
        const canvas = canvasRef.current;
        if (!canvas || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => { if (!hasInk.current) prepareCanvas(); });
        ro.observe(canvas);
        return () => ro.disconnect();
    }, [prepareCanvas]);

    const getPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        // Pointer events carry clientX/clientY for mouse, pen and touch alike.
        // Map client coords to bitmap coords, correcting any CSS↔bitmap size drift.
        const scaleX = rect.width ? canvas.width / rect.width : 1;
        const scaleY = rect.height ? canvas.height / rect.height : 1;
        return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    };

    const start = useCallback((e) => {
        e.preventDefault();
        // Capture so a pen/finger stroke keeps tracking if it leaves the pad.
        canvasRef.current?.setPointerCapture?.(e.pointerId);
        // Ensure the bitmap matches the on-screen size before the first stroke;
        // safe to resize here because the pad is still empty.
        if (!hasInk.current) prepareCanvas();
        drawing.current = true;
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = getPoint(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    }, [prepareCanvas]);

    const move = useCallback((e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = getPoint(e);
        ctx.lineTo(x, y);
        ctx.stroke();
        if (!hasInk.current) {
            hasInk.current = true;
            setIsEmpty(false);
        }
    }, []);

    const end = useCallback(() => {
        if (!drawing.current) return;
        drawing.current = false;
        if (!hasInk.current) return; // nothing drawn — don't emit a blank image
        if (onChange) onChange(exportWhitePng(canvasRef.current));
    }, [onChange]);

    const clear = () => {
        const canvas = canvasRef.current;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        hasInk.current = false;
        setIsEmpty(true);
        if (onChange) onChange(null);
    };

    return (
        <Box>
            <Box
                sx={{
                    position: 'relative',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: '#fff',
                    touchAction: 'none',
                }}
            >
                {isEmpty && (
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}
                    >
                        {label}
                    </Typography>
                )}
                <canvas
                    ref={canvasRef}
                    style={{ width: '100%', height, display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                    onPointerDown={start}
                    onPointerMove={move}
                    onPointerUp={end}
                    onPointerLeave={end}
                    onPointerCancel={end}
                />
            </Box>
            <Button size="small" onClick={clear} sx={{ mt: 0.5 }} disabled={isEmpty}>
                Clear signature
            </Button>
        </Box>
    );
}
